"use client";

import { useCallback, useEffect, useState } from "react";
import { FormStatus } from "@/components/ui/Form";
import {
  fetchUserAdverts,
  deleteUserAdvert,
  type UserAdvert,
  type AdvertPagination,
} from "@/lib/api/c-advertisements-user";
import { confirmDialog } from "@/lib/notifications/confirmDialog";
import { AdvertList } from "./AdvertList";
import { AdvertForm } from "./AdvertForm";
import styles from "./CAdvertisements.module.css";

/**
 * Plugin ogłoszeń usera (shell c_user_accounts) - REST/React.
 *
 * Akcje hash-routable (z `sharingContentForApi`): `adverts` (lista),
 * `new-advert` (formularz dodania). Edycja konkretnego ogłoszenia jest
 * WEWNETRZNYM stanem (`mode`) - ProfileTabs matchuje tylko zarejestrowane
 * akcje, więc dynamiczne id nie przechodzi przez hash.
 *
 * Stan `mode`:
 *   - list   -> AdvertList (lista + per-wiersz edytuj/usuń + przycisk dodaj)
 *   - create -> AdvertForm (nowe ogłoszenie), po zapisie -> edit dla nowego id
 *               (żeby user od razu dodał zdjęcia inline w tym samym formularzu)
 *   - edit   -> AdvertForm (edycja, advertId; zdjęcia inline w sekcji formularza)
 */
type Mode =
  | { type: "list" }
  | { type: "create" }
  | { type: "edit"; id: number };

export function CAdvertisementsView({ actionId }: { actionId: string }) {
  const [mode, setMode] = useState<Mode>(
    actionId === "new-advert" ? { type: "create" } : { type: "list" },
  );

  const [adverts, setAdverts] = useState<UserAdvert[]>([]);
  const [pagination, setPagination] = useState<AdvertPagination | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { adverts: rows, pagination: p } = await fetchUserAdverts(page, 20);
      setAdverts(rows);
      setPagination(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    if (mode.type === "list") void reload();
  }, [mode, reload]);

  const handleDelete = async (a: UserAdvert) => {
    const ok = await confirmDialog({
      title: "Usunąć ogłoszenie?",
      message: `Ogłoszenie "${a.name}" wraz ze wszystkimi zdjęciami zostanie trwale usunięte.`,
      confirmLabel: "Usuń",
      cancelLabel: "Anuluj",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await deleteUserAdvert(a.id);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  if (mode.type === "create") {
    /* Po utworzeniu nowego advertu - przerzucamy usera do trybu edycji świeżo
       utworzonego id. Formularz w edycji ma inline sekcję zdjęć, więc user
       moze od razu wgrać obrazki bez oddzielnej nawigacji. */
    return (
      <AdvertForm
        onSaved={(newId) => setMode({ type: "edit", id: newId })}
        onCancel={() => setMode({ type: "list" })}
      />
    );
  }

  if (mode.type === "edit") {
    return (
      <AdvertForm
        advertId={mode.id}
        onSaved={() => setMode({ type: "list" })}
        onCancel={() => setMode({ type: "list" })}
      />
    );
  }

  return (
    <div className={styles.root}>
      {error && <FormStatus variant="error">{error}</FormStatus>}
      <AdvertList
        adverts={adverts}
        pagination={pagination}
        page={page}
        loading={loading}
        onPageChange={setPage}
        onNew={() => setMode({ type: "create" })}
        onEdit={(a) => setMode({ type: "edit", id: a.id })}
        onDelete={handleDelete}
      />
    </div>
  );
}

/**
 * Czy plugin obsługuje daną akcję (registry w ExtensionActionView).
 * Tylko akcje hash-routable z `sharingContentForApi`.
 */
export function cAdvertisementsHandlesAction(actionId: string): boolean {
  return actionId === "adverts" || actionId === "new-advert";
}
